/**
 * Painel lateral direito do MapPage (/#map).
 *
 * 5 seções colapsáveis: alarmes (enlaces DOWN/DEGRADED com idade, sempre no topo),
 * busca de endereço (Geocoder), POPs (filtra + foca),
 * Frota (toggle + filtro status + search por motorista/placa/grupo + lista clicável),
 * Enlaces (filtro por estado + abrir LinkEditor).
 * Recolhido, o handle mostra badge pulsante com a contagem de DOWN (âmbar se só DEGRADED).
 *
 * Foco no mapa via callback onFocus(latlng, zoom) que o MapPage encadeia no <MapFocus> via tick.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Pop, type PopLink } from '../api';

type Vehicle = {
  id: string; label: string; plate: string | null; group: string | null; model: string | null;
  lat: number | null; lng: number | null; status: 'online' | 'idle' | 'offline' | 'never';
  ageSec: number | null; speedKmh: number | null;
};
type FleetResp = { summary: { total: number; online: number; idle: number; offline: number; never: number }; vehicles: Vehicle[] };
type FleetFilter = 'all' | 'online' | 'idle' | 'offline';

const LINK_STATE_COLOR: Record<PopLink['state'], string> = {
  UP: '#10b981', DEGRADED: '#f59e0b', DOWN: '#ef4444', UNKNOWN: '#64748b'
};
const STATUS_COLOR: Record<Vehicle['status'], string> = {
  online: '#10b981', idle: '#f59e0b', offline: '#64748b', never: '#475569'
};

export function MapSidePanel({
  pops, visibleLinks, onFocus, onEditLink, onFitLink, onResetView,
  fleetEnabled, setFleetEnabled, fleetFilter, setFleetFilter
}: {
  pops: Pop[];
  visibleLinks: PopLink[];
  onFocus: (latlng: { lat: number; lng: number }, zoom?: number) => void;
  onEditLink: (link: PopLink) => void;
  onFitLink: (link: PopLink) => void;
  onResetView: () => void;
  fleetEnabled: boolean;
  setFleetEnabled: (b: boolean) => void;
  fleetFilter: FleetFilter;
  setFleetFilter: (f: FleetFilter) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const alarmCounts = useMemo(() => {
    let down = 0, degraded = 0;
    for (const l of visibleLinks) {
      if (l.state === 'DOWN') down++;
      else if (l.state === 'DEGRADED') degraded++;
    }
    return { down, degraded };
  }, [visibleLinks]);

  if (collapsed) {
    return (
      <div data-drag-handle className="flex flex-col items-center rounded-l bg-slate-800 ring-1 ring-slate-700 shadow-lg" style={{ cursor: 'move' }}>
        {(alarmCounts.down > 0 || alarmCounts.degraded > 0) && (
          <span
            title={`${alarmCounts.down} enlace(s) DOWN · ${alarmCounts.degraded} DEGRADED`}
            className={`mt-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white animate-pulse ${alarmCounts.down > 0 ? 'bg-rose-600' : 'bg-amber-600'}`}>
            🚨 {alarmCounts.down > 0 ? alarmCounts.down : alarmCounts.degraded}
          </span>
        )}
        <button onClick={() => setCollapsed(false)}
          title="Abrir painel lateral (arraste pra mover)"
          className="px-2 py-3 text-xs rounded-l text-slate-200 hover:bg-slate-700"
          style={{ writingMode: 'vertical-rl' }}>
          ❮ painel
        </button>
      </div>
    );
  }

  return (
    <aside className="w-80 bg-slate-900 ring-1 ring-slate-800 rounded overflow-y-auto shadow-lg"
           style={{ maxHeight: 'calc(100vh - 120px)' }}>
      <div data-drag-handle
           className="flex items-center justify-between px-3 py-2 border-b border-slate-800 sticky top-0 bg-slate-900 z-10"
           style={{ cursor: 'move' }}
           title="arraste pra mover o painel">
        <span className="text-xs font-semibold text-slate-300">⋮⋮ Ferramentas do mapa</span>
        <span className="flex items-center gap-1">
          <button onClick={onResetView} title="Visão geral — encaixar todos os POPs" data-no-drag
            className="text-xs text-slate-400 hover:text-slate-200 px-1" style={{ cursor: 'pointer' }}>⌂</button>
          <button onClick={() => setCollapsed(true)} title="Recolher painel" data-no-drag
            className="text-xs text-slate-400 hover:text-slate-200 px-1" style={{ cursor: 'pointer' }}>❯</button>
        </span>
      </div>

      <AlarmsSection links={visibleLinks} onEditLink={onEditLink} onFitLink={onFitLink} />
      <AddressSection onFocus={onFocus} />
      <PopsSection pops={pops} onFocus={onFocus} />
      <FleetSection
        enabled={fleetEnabled} setEnabled={setFleetEnabled}
        filter={fleetFilter} setFilter={setFleetFilter}
        onFocus={onFocus}
      />
      <LinksSection links={visibleLinks} onEditLink={onEditLink} onFocus={onFocus} />
    </aside>
  );
}

/* ---------------- 🚨 Alarmes (enlaces DOWN/DEGRADED) ---------------- */
function alarmAgeLabel(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} d`;
}

function AlarmLinkItem({ link, onEditLink, onFitLink }: {
  link: PopLink;
  onEditLink: (l: PopLink) => void;
  onFitLink: (l: PopLink) => void;
}) {
  const isDown = link.state === 'DOWN';
  const color = LINK_STATE_COLOR[link.state];
  const age = alarmAgeLabel(link.lastChange);
  return (
    <li className={`rounded bg-slate-950 ring-1 ${isDown ? 'ring-rose-800/70 hover:ring-rose-600' : 'ring-amber-800/70 hover:ring-amber-600'}`}>
      <button onClick={() => onFitLink(link)} title="Centralizar o mapa no trecho"
        className="w-full text-left px-2 py-1.5 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ background: color }} />
          <span className="text-slate-200 truncate flex-1">{link.sourcePop.name} ↔ {link.destPop.name}</span>
          <span className="text-[10px] font-bold" style={{ color }}>{isDown ? 'DOWN' : 'DEGRADED'}</span>
        </div>
        <div className="text-slate-500 text-[10px] truncate">{link.sourceDevice.hostname} ↔ {link.destDevice.hostname}</div>
        {age && <div className="text-[10px] font-semibold" style={{ color }}>{isDown ? 'down' : 'degradado'} {age}</div>}
      </button>
      <button onClick={() => onEditLink(link)}
        className="w-full text-left px-2 pb-1 text-[10px] text-sky-400 hover:underline">
        ✏ abrir detalhes do enlace
      </button>
    </li>
  );
}

function AlarmsSection({ links, onEditLink, onFitLink }: {
  links: PopLink[];
  onEditLink: (l: PopLink) => void;
  onFitLink: (l: PopLink) => void;
}) {
  const byLastChangeDesc = (a: PopLink, b: PopLink) =>
    (b.lastChange ? Date.parse(b.lastChange) : 0) - (a.lastChange ? Date.parse(a.lastChange) : 0);
  const down = useMemo(() => links.filter((l) => l.state === 'DOWN').sort(byLastChangeDesc), [links]);
  const degraded = useMemo(() => links.filter((l) => l.state === 'DEGRADED').sort(byLastChangeDesc), [links]);
  const unknown = useMemo(() => links.filter((l) => l.state === 'UNKNOWN').length, [links]);
  const total = down.length + degraded.length;

  return (
    <details open className="border-b border-slate-800">
      <summary className="px-3 py-2 text-xs font-semibold text-slate-200 cursor-pointer hover:bg-slate-800/50">
        🚨 Alarmes{' '}
        {total > 0 ? (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white ${down.length > 0 ? 'bg-rose-600' : 'bg-amber-600'}`}>
            {total}
          </span>
        ) : (
          <span className="text-emerald-400 font-normal">✓</span>
        )}
      </summary>
      <div className="px-3 pb-3 space-y-2">
        {total === 0 && (
          <div className="text-[11px] text-emerald-400 px-2 py-1.5 rounded bg-emerald-500/10 ring-1 ring-emerald-800/40">
            ✓ todos os enlaces monitorados estão UP
          </div>
        )}
        {down.length > 0 && (
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {down.map((l) => <AlarmLinkItem key={l.id} link={l} onEditLink={onEditLink} onFitLink={onFitLink} />)}
          </ul>
        )}
        {degraded.length > 0 && (
          <>
            <div className="text-[10px] text-amber-400 font-semibold px-1">degradados (um lado down)</div>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {degraded.map((l) => <AlarmLinkItem key={l.id} link={l} onEditLink={onEditLink} onFitLink={onFitLink} />)}
            </ul>
          </>
        )}
        {unknown > 0 && (
          <div className="text-[10px] text-slate-500 px-1">{unknown} enlace(s) sem monitoramento (UNKNOWN)</div>
        )}
      </div>
    </details>
  );
}

/* ---------------- 🔎 Endereço (Geocoder) ---------------- */
function AddressSection({ onFocus }: { onFocus: (p: { lat: number; lng: number }, zoom?: number) => void }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [last, setLast] = useState<{ label: string; lat: number; lng: number } | null>(null);

  async function search() {
    if (!q.trim()) return;
    if (!window.google?.maps) { setErr('Maps ainda carregando'); return; }
    setBusy(true); setErr(null);
    try {
      const geocoder = new (window as any).google.maps.Geocoder();
      const r: any = await geocoder.geocode({ address: q.trim(), region: 'br' });
      const first = r?.results?.[0];
      if (!first) { setErr('endereço não encontrado'); return; }
      const loc = first.geometry.location;
      const latlng = { lat: loc.lat(), lng: loc.lng() };
      setLast({ label: first.formatted_address, ...latlng });
      onFocus(latlng, 14);
    } catch (e: any) {
      setErr(`erro: ${e?.message ?? e?.code ?? 'falhou'}`);
    } finally { setBusy(false); }
  }

  return (
    <details open className="border-b border-slate-800">
      <summary className="px-3 py-2 text-xs font-semibold text-slate-200 cursor-pointer hover:bg-slate-800/50">🔎 Buscar endereço</summary>
      <div className="px-3 pb-3 space-y-2">
        <div className="flex gap-1">
          <input className="flex-1 bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="ex.: Maceió, ou rua/CEP" />
          <button onClick={search} disabled={busy}
            className="px-2 py-1 text-xs rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50">
            {busy ? '...' : 'Ir'}
          </button>
        </div>
        {err && <div className="text-[11px] text-rose-400">{err}</div>}
        {last && (
          <button onClick={() => onFocus({ lat: last.lat, lng: last.lng }, 14)}
            className="block w-full text-left p-2 text-[11px] rounded bg-slate-950 ring-1 ring-slate-800 hover:ring-slate-700">
            <div className="text-slate-200 truncate">{last.label}</div>
            <div className="text-slate-500 font-mono text-[10px]">{last.lat.toFixed(5)}, {last.lng.toFixed(5)}</div>
          </button>
        )}
      </div>
    </details>
  );
}

/* ---------------- 📍 POPs ---------------- */
function PopsSection({ pops, onFocus }: { pops: Pop[]; onFocus: (p: { lat: number; lng: number }, zoom?: number) => void }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    const withCoords = pops.filter((p) => p.latitude != null && p.longitude != null);
    if (!norm) return withCoords.slice(0, 100);
    return withCoords.filter((p) => {
      const hay = `${p.name} ${p.code} ${p.city ?? ''} ${p.uf ?? ''}`.toLowerCase();
      return hay.includes(norm);
    }).slice(0, 100);
  }, [pops, q]);

  return (
    <details open className="border-b border-slate-800">
      <summary className="px-3 py-2 text-xs font-semibold text-slate-200 cursor-pointer hover:bg-slate-800/50">
        📍 POPs <span className="text-slate-500 font-normal">({pops.filter((p) => p.latitude != null).length})</span>
      </summary>
      <div className="px-3 pb-3 space-y-2">
        <input className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs"
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar por nome/code/cidade/UF" />
        <ul className="space-y-1 max-h-60 overflow-y-auto">
          {filtered.map((p) => (
            <li key={p.id}>
              <button onClick={() => onFocus({ lat: p.latitude!, lng: p.longitude! }, 13)}
                className="w-full text-left px-2 py-1.5 rounded bg-slate-950 hover:bg-slate-800 ring-1 ring-slate-800 hover:ring-slate-700 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-400 text-[10px] font-mono">{p.uf ?? '--'}</span>
                  <span className="text-slate-200 truncate">{p.name}</span>
                </div>
                <div className="text-slate-500 text-[10px]">{p.city ?? '—'} · <code>{p.code}</code></div>
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="text-[11px] text-slate-500 px-2">sem resultados</li>}
        </ul>
      </div>
    </details>
  );
}

/* ---------------- 🚗 Frota ---------------- */
function FleetSection({
  enabled, setEnabled, filter, setFilter, onFocus
}: {
  enabled: boolean; setEnabled: (b: boolean) => void;
  filter: FleetFilter; setFilter: (f: FleetFilter) => void;
  onFocus: (p: { lat: number; lng: number }, zoom?: number) => void;
}) {
  const [q, setQ] = useState('');
  const fleetQ = useQuery<FleetResp>({
    queryKey: ['fleet', 'vehicles'],
    queryFn: () => api.get('/api/fleet/vehicles'),
    refetchInterval: 30_000,
    enabled
  });
  const summary = fleetQ.data?.summary;
  const filtered = useMemo(() => {
    if (!fleetQ.data) return [] as Vehicle[];
    const norm = q.trim().toLowerCase();
    return fleetQ.data.vehicles
      .filter((v) => v.lat != null && v.lng != null)
      .filter((v) => filter === 'all' ? true : v.status === filter)
      .filter((v) => {
        if (!norm) return true;
        const hay = `${v.label} ${v.plate ?? ''} ${v.group ?? ''} ${v.model ?? ''}`.toLowerCase();
        return hay.includes(norm);
      })
      .slice(0, 150);
  }, [fleetQ.data, q, filter]);

  return (
    <details open className="border-b border-slate-800">
      <summary className="px-3 py-2 text-xs font-semibold text-slate-200 cursor-pointer hover:bg-slate-800/50">
        🚗 Frota {summary && <span className="text-slate-500 font-normal">({summary.total})</span>}
      </summary>
      <div className="px-3 pb-3 space-y-2">
        <div className="flex gap-1 items-center">
          <button onClick={() => setEnabled(!enabled)}
            className={`px-2 py-1 text-xs rounded ${enabled ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-300'} hover:bg-cyan-500`}>
            {enabled ? '✓ mostrar' : 'mostrar no mapa'}
          </button>
          {enabled && summary && (
            <select className="bg-slate-800 border border-slate-700 rounded px-1 py-1 text-xs"
              value={filter} onChange={(e) => setFilter(e.target.value as FleetFilter)}>
              <option value="all">todos ({summary.total})</option>
              <option value="online">online ({summary.online})</option>
              <option value="idle">parados ({summary.idle})</option>
              <option value="offline">offline ({summary.offline})</option>
            </select>
          )}
        </div>
        {enabled && (
          <>
            <input className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="buscar motorista, placa ou grupo" />
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {filtered.map((v) => (
                <li key={v.id}>
                  <button onClick={() => onFocus({ lat: v.lat!, lng: v.lng! }, 15)}
                    className="w-full text-left px-2 py-1.5 rounded bg-slate-950 hover:bg-slate-800 ring-1 ring-slate-800 hover:ring-slate-700 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[v.status] }} />
                      <span className="text-slate-200 truncate flex-1">{v.label}</span>
                      {v.plate && <code className="text-[10px] text-slate-400">{v.plate}</code>}
                    </div>
                    {v.group && <div className="text-slate-500 text-[10px] truncate">{v.group}</div>}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="text-[11px] text-slate-500 px-2">{fleetQ.isLoading ? 'carregando…' : 'sem resultados'}</li>}
            </ul>
          </>
        )}
      </div>
    </details>
  );
}

/* ---------------- 🔗 Enlaces ---------------- */
function LinksSection({
  links, onEditLink, onFocus
}: {
  links: PopLink[];
  onEditLink: (l: PopLink) => void;
  onFocus: (p: { lat: number; lng: number }, zoom?: number) => void;
}) {
  const [stateFilter, setStateFilter] = useState<'all' | PopLink['state']>('all');
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return links
      .filter((l) => stateFilter === 'all' ? true : l.state === stateFilter)
      .filter((l) => {
        if (!norm) return true;
        const hay = `${l.sourcePop.name} ${l.destPop.name} ${l.sourceDevice.hostname} ${l.destDevice.hostname}`.toLowerCase();
        return hay.includes(norm);
      })
      .slice(0, 100);
  }, [links, stateFilter, q]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: links.length, UP: 0, DEGRADED: 0, DOWN: 0, UNKNOWN: 0 };
    for (const l of links) c[l.state] = (c[l.state] ?? 0) + 1;
    return c;
  }, [links]);

  return (
    <details className="border-b border-slate-800">
      <summary className="px-3 py-2 text-xs font-semibold text-slate-200 cursor-pointer hover:bg-slate-800/50">
        🔗 Enlaces <span className="text-slate-500 font-normal">({links.length})</span>
      </summary>
      <div className="px-3 pb-3 space-y-2">
        <select className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs"
          value={stateFilter} onChange={(e) => setStateFilter(e.target.value as any)}>
          <option value="all">todos os estados ({counts.all})</option>
          <option value="UP">UP ({counts.UP})</option>
          <option value="DEGRADED">DEGRADED ({counts.DEGRADED})</option>
          <option value="DOWN">DOWN ({counts.DOWN})</option>
          <option value="UNKNOWN">UNKNOWN ({counts.UNKNOWN})</option>
        </select>
        <input className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs"
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar por POP ou hostname" />
        <ul className="space-y-1 max-h-60 overflow-y-auto">
          {filtered.map((l) => (
            <li key={l.id} className="rounded bg-slate-950 ring-1 ring-slate-800 hover:ring-slate-700">
              <button onClick={() => onEditLink(l)}
                className="w-full text-left px-2 py-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: LINK_STATE_COLOR[l.state] }} />
                  <span className="text-slate-200 truncate flex-1">{l.sourcePop.name} ↔ {l.destPop.name}</span>
                  <span className="text-[10px] text-slate-500">{l.type}</span>
                </div>
                <div className="text-slate-500 text-[10px] truncate">{l.sourceDevice.hostname} ↔ {l.destDevice.hostname}</div>
              </button>
              <button
                onClick={() => {
                  if (l.sourcePop.latitude == null || l.sourcePop.longitude == null) return;
                  const lat = (l.sourcePop.latitude! + l.destPop.latitude!) / 2;
                  const lng = (l.sourcePop.longitude! + l.destPop.longitude!) / 2;
                  onFocus({ lat, lng }, 9);
                }}
                className="w-full text-left px-2 pb-1 text-[10px] text-sky-400 hover:underline">
                ↔ focar no mapa
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="text-[11px] text-slate-500 px-2">sem resultados</li>}
        </ul>
      </div>
    </details>
  );
}
